import { AfterViewInit, Component } from '@angular/core';
import { FspPremissesGridDataService } from './fsp-premisses-grid-data.service';
import { FspPremissesGridDto } from './fsp-premisses-grid-data-model';
import {
  fspPremissesGridColumns,
  getFspPremissesGridToolbarItems,
} from './fsp-premisses-grid-model';
import { SelectableSettings } from '@progress/kendo-angular-grid';
import { FreeServicesParcelRequestGridDto } from '../../fsp-parcel-request-grid/free-services-parcel-request-grid-data-model';
import { combineLatest, filter, map, Observable, takeWhile, tap } from 'rxjs';
import { FreeServicesParcelRequestGridDataService } from '../../fsp-parcel-request-grid/free-services-parcel-request-grid-data.service';
import { GkKendoGridComponent } from '../../../../gk-kendo';
import { RequestType } from '../../../../gk-dynamic-list/gk-dynamic-list.model';
import { FreeServicesRequestListGridDto } from '../../../free-services-request-list-grid/free-services-request-list-grid-data-model';
import { FreeServicesRequestListGridDataService } from '../../../free-services-request-list-grid/free-services-request-list-grid-data.service';

@Component({
  selector: 'app-fsp-premisses-grid',
  templateUrl:
    '../../../../gk-kendo/gk-kendo-grid/gk-kendo-grid.component.html',
  standalone: false,
})
export class FspPremissesGridComponent
  extends GkKendoGridComponent<FspPremissesGridDto>
  implements AfterViewInit
{
  constructor(
    public fspPremissesGridDataService: FspPremissesGridDataService,
    public freeServicesParcelRequestGridDataService: FreeServicesParcelRequestGridDataService,
    public freeServicesRequestListGridDataService: FreeServicesRequestListGridDataService,
  ) {
    super(fspPremissesGridDataService);
  }

  override topGridToolbarItems = getFspPremissesGridToolbarItems(this);
  override columns = fspPremissesGridColumns;
  override selectable: SelectableSettings = { enabled: true, mode: 'single' };
  override sortable = true;

  selectedParcelGridItem: FreeServicesParcelRequestGridDto;
  selectedPremissesGridItem: FspPremissesGridDto;
  selectedRequestListGridItem: FreeServicesRequestListGridDto;

  override ngAfterViewInit(): void {
    this.subscribeToAllSelections();
  }

  private subscribeToAllSelections(): void {
    const parcel$ = this.subscribeToParcelListGridSelectedKeys();
    const request$ = this.subscribeToRequestListGridSelectedKeys();
    const premisses$ = this.subscribeToPremissesGridSelectedKeys();

    combineLatest([parcel$, request$])
      .pipe(takeWhile(() => this.isAlive))
      .subscribe(([parcelItem, requestItem]) => {
        this.selectedParcelGridItem = parcelItem;
        this.selectedRequestListGridItem = requestItem;
        this.fetchGridData();
      });

    premisses$
      .pipe(takeWhile(() => this.isAlive))
      .subscribe((premissesItem) => {
        this.selectedPremissesGridItem = premissesItem;
      });
  }

  subscribeToParcelListGridSelectedKeys(): Observable<FreeServicesParcelRequestGridDto> {
    return this.freeServicesParcelRequestGridDataService.$selection.pipe(
      takeWhile(() => this.isAlive),
      tap(() => {
        this.selectedParcelGridItem = undefined;
      }),
      filter(Boolean),
      map(
        (selection) =>
          selection.selectedRows[0]
            .dataItem as FreeServicesParcelRequestGridDto,
      ),
    );
  }

  subscribeToRequestListGridSelectedKeys(): Observable<FreeServicesRequestListGridDto> {
    return this.freeServicesRequestListGridDataService.$selection.pipe(
      takeWhile(() => this.isAlive),
      tap(() => {
        this.selectedRequestListGridItem = undefined;
      }),
      filter(Boolean),
      map(
        (selection) =>
          selection.selectedRows[0].dataItem as FreeServicesRequestListGridDto,
      ),
    );
  }

  subscribeToPremissesGridSelectedKeys(): Observable<FspPremissesGridDto> {
    return this.fspPremissesGridDataService.$selection.pipe(
      takeWhile(() => this.isAlive),
      tap(() => {
        this.selectedPremissesGridItem = undefined;
      }),
      filter(Boolean),
      map(
        (selection) =>
          selection.selectedRows[0].dataItem as FspPremissesGridDto,
      ),
    );
  }

  fetchGridData(): void {
    this.fspPremissesGridDataService
      .queryByState(
        null,
        null,
        `/api/interesant/przp/zamowienie/${this.selectedRequestListGridItem.ZamowUuid}/dzialka/${this.selectedParcelGridItem.KdzialkaUuid}/lokale`,
      )
      .subscribe((response) => {
        if (!response.data.length) {
          this.toastr.warning(
            this.translateService.instant('FREE_PORTAL_SERVICES.NO_PREMISSES'),
          );
        }
      });
  }

  downloadReport(): void {
    if (!this.showMessageIfNoRowsSelected()) {
      return;
    }

    this.downloadService.downloadAndSaveFile(
      `/api/interesant/przp/zamowienie/${this.selectedRequestListGridItem.ZamowUuid}/dzialka/${this.selectedParcelGridItem.KdzialkaUuid}/lokal/${this.selectedPremissesGridItem.Uuid}/wydruk`,
      {},
      RequestType.Post,
    );
  }
}
